// Implements: F-IDN-017, F-UIX-013
/**
 * "¿Borrar tu cuenta y tus datos personales?" (v3 `eraseAccount`): a warning, and the e-mail to type
 * before "Borrar definitivamente" enables (the exact address, case and outer spaces ignored). Pure
 * presentation; the dialog hands the focus back to its opener when it closes (`Dialog`).
 *
 * The warning says up front that the personal mockups, the trash included, are deleted for good with the
 * account and that their shared links stop working (owner 2026-10-19, 3B); no count, so nothing about
 * them is read here.
 *
 * A refusal says why: while the person still owns team or organization mockups retrying can never work,
 * so the notice names the reason and the steps that really hand them over; any other failure is the
 * retry line.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import type { EraseRefusal } from "../model/eraseRefusal";
import { confirmsEmail } from "../model/exportName";
import shared from "./account.module.css";

export interface EraseAccountDialogProps {
  readonly open: boolean;
  readonly email: string;
  readonly busy: boolean;
  /** Why the last attempt was refused, or `null` while nothing failed. */
  readonly refusal: EraseRefusal | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function EraseAccountDialog({ open, email, busy, refusal, onConfirm, onCancel }: EraseAccountDialogProps): ReactElement {
  const { t } = useTranslation();
  const fieldId = useId();
  const [typed, setTyped] = useState("");
  const confirmed = confirmsEmail(typed, email);

  return (
    <Dialog
      open={open}
      title={t("account.data.eraseDialogTitle")}
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t("common.action.cancel")}
          </Button>
          <Button variant="danger" disabled={!confirmed || busy} loading={busy} onClick={onConfirm}>
            {t("account.data.eraseConfirm")}
          </Button>
        </>
      }
    >
      <div className={shared.noticeDanger}>
        <Icon name="alert-triangle" size="sm" />
        <div>
          <p className={shared.noticeText}>{t("account.data.eraseWarning")}</p>
          <p className={shared.noticeText}>{t("account.data.erasePersonalMockups")}</p>
        </div>
      </div>
      <div className={shared.field}>
        <label className={shared.label} htmlFor={fieldId}>
          {t("account.data.eraseConfirmLabel", { email })}
        </label>
        <Input
          id={fieldId}
          value={typed}
          autoComplete="off"
          onChange={setTyped}
          onEnter={() => {
            if (confirmed && !busy) onConfirm();
          }}
        />
      </div>
      {refusal === "stillResponsible" ? (
        <div className={shared.noticeDanger} role="alert">
          <Icon name="alert-triangle" size="sm" />
          <div>
            <p className={shared.noticeText}>{t("account.data.eraseStillResponsible")}</p>
            <ul className={shared.steps}>
              <li>{t("account.data.eraseStepShared")}</li>
              <li>{t("account.data.eraseStepTrash")}</li>
            </ul>
          </div>
        </div>
      ) : null}
      {refusal === "failed" ? (
        <p className={shared.error} role="alert">
          {t("account.data.eraseFailed")}
        </p>
      ) : null}
    </Dialog>
  );
}
